/**
 * <cart-upsell> — "Bieži pērk kopā" in the cart drawer.
 *
 * Every line in the cart is a seed, not just the first one. Each seed contributes its own
 * line's neighbours first — Photoderm beside Photoderm, Intensive NAD+ beside NAD+ — so the
 * row follows the cart instead of being picked once from whatever was added first.
 *
 * Sources, in order of trust:
 *   1. /recommendations/products.json?intent=related — full product objects, variants included.
 *   2. /search/suggest.json, queried with the seed's line name, used only when the related
 *      list holds nothing from that line (short lines like NAD+ are the usual case). Search
 *      results carry no variants, so a pick from there is hydrated from /products/<handle>.js
 *      before it is offered as one tap.
 *
 * Ranking: same sub-line, same line, another size of what is already in the cart, same brand.
 * One tile per cart line in the first round, then a second per line until the row is full.
 */

const RELATED_LIMIT = 10;
const SEARCH_LIMIT = 8;
const MAX_SEEDS = 6;
const MAX_TILES = 6;
const MIN_TILES = 4;

// Module scope on purpose: Horizon re-renders the whole cart section on every cart change,
// which destroys and re-creates this element. A cache on the instance would be refetched
// on every single add.
const relatedCache = new Map();
const searchCache = new Map();
const productCache = new Map();

class CartUpsell extends HTMLElement {
  #timer = 0;
  #observer = null;
  #run = 0;

  connectedCallback() {
    this.#schedule();
    document.addEventListener('cart:update', this.#schedule);

    // The cart section is re-rendered server-side whenever the cart changes and when the
    // drawer opens, which puts the empty server markup back. Watch for that and refill
    // instead of assuming a single render is enough.
    this.#observer = new MutationObserver(() => {
      const tiles = this.querySelector('[data-tiles]');
      if (tiles && tiles.children.length === 0) this.#schedule();
    });
    this.#observer.observe(this, { childList: true, subtree: true });
  }

  disconnectedCallback() {
    clearTimeout(this.#timer);
    document.removeEventListener('cart:update', this.#schedule);
    this.#observer?.disconnect();
  }

  #schedule = () => {
    clearTimeout(this.#timer);
    this.#timer = setTimeout(() => this.#render(), 50);
  };

  get #root() {
    return this.dataset.root || '/';
  }

  #seeds() {
    const script = this.querySelector('script[data-seeds]');
    if (!script) return [];
    try {
      const seeds = JSON.parse(script.textContent);
      return Array.isArray(seeds) ? seeds.map((seed) => ({ ...seed, id: String(seed.id) })) : [];
    } catch {
      return [];
    }
  }

  async #render() {
    const run = ++this.#run;
    const seeds = this.#seeds();
    if (seeds.length === 0) return;

    const tilesBefore = this.querySelector('[data-tiles]');
    if (!tilesBefore || tilesBefore.children.length > 0) return;

    const inCart = new Set((this.dataset.exclude || '').split(',').filter(Boolean));
    const lines = byLine(seeds).slice(-MAX_SEEDS);

    // The row is sized by what is in the cart, but picked from the distinct lines: a cart
    // of six products asks for six tiles even when two of them are from the same line — the
    // extra tile is then a second suggestion from the line that has the most to offer.
    const target = Math.min(MAX_TILES, Math.max(MIN_TILES, seeds.length));

    const lists = await Promise.all(lines.map((seed) => this.#candidates(seed, inCart)));
    const picks = await this.#hydrate(interleave(lists, target));

    // Another cart change may have overtaken this render while it was fetching.
    if (run !== this.#run) return;

    const tiles = this.querySelector('[data-tiles]');
    if (!tiles || tiles.children.length > 0 || picks.length === 0) return;

    tiles.innerHTML = picks.map((pick) => tile(pick)).join('');
    tiles.addEventListener('click', this.#onClick);

    const note = this.querySelector('[data-count]');
    if (note) {
      note.textContent = `${picks.length} ${picks.length === 1 ? 'ieteikums' : 'ieteikumi'}`;
      note.hidden = false;
    }

    this.hidden = false;
  }

  async #candidates(seed, inCart) {
    const { one, two } = lineTokens(seed.title, seed.vendor);
    const products = await this.#related(seed.id);
    const scored = (list) => rank(list, seed, { one, two }, inCart);

    let candidates = scored(products);

    // Ask search for the line by name when the related list is thin on that line. Two is the
    // bar because a seed can be asked for a second tile once every line has had its first —
    // Intensive NAD+ is three products, and related returns one of them.
    if (countSameLine(candidates) < 2) {
      for (const query of [two, one]) {
        if (!query) continue;
        candidates = scored(products.concat(await this.#search(query)));
        if (countSameLine(candidates) >= 2) break;
      }
    }

    return candidates;
  }

  async #related(productId) {
    if (relatedCache.has(productId)) return relatedCache.get(productId);
    const url = `${this.#root}recommendations/products.json?product_id=${encodeURIComponent(productId)}&limit=${RELATED_LIMIT}&intent=related`;
    const body = await getJson(url);
    const products = Array.isArray(body?.products) ? body.products.map(normalize) : [];
    relatedCache.set(productId, products);
    return products;
  }

  async #search(query) {
    const key = query.toLowerCase();
    if (searchCache.has(key)) return searchCache.get(key);
    const url = `${this.#root}search/suggest.json?q=${encodeURIComponent(query)}`
      + `&resources[type]=product&resources[limit]=${SEARCH_LIMIT}`
      + '&resources[options][unavailable_products]=hide';
    const body = await getJson(url);
    const found = body?.resources?.results?.products;
    const products = Array.isArray(found) ? found.map(normalize) : [];
    searchCache.set(key, products);
    return products;
  }

  // Search results carry no variants, so a pick taken from search cannot be added in one tap
  // until its product JSON says it has exactly one. Anything else is dropped rather than
  // shown as a tile that would need a size chosen.
  async #hydrate(picks) {
    await Promise.all(picks.map(async (pick) => {
      if (pick.product.variantId || !pick.product.handle) return;
      const data = await this.#product(pick.product.handle);
      const variants = data?.variants || [];
      if (!data || data.available === false || variants.length !== 1) {
        pick.drop = true;
        return;
      }
      pick.product.variantId = String(variants[0].id);

      if (!pick.product.compareAt) {
        const was = toCents(variants[0].compare_at_price ?? data.compare_at_price);
        pick.product.compareAt = was > pick.product.price ? was : 0;
      }
    }));
    return picks.filter((pick) => !pick.drop && pick.product.variantId);
  }

  async #product(handle) {
    if (productCache.has(handle)) return productCache.get(handle);
    const data = await getJson(`${this.#root}products/${encodeURIComponent(handle)}.js`);
    productCache.set(handle, data);
    return data;
  }

  #onClick = async (event) => {
    const button = event.target.closest('.ms-upsell__add');
    if (!button) return;

    button.disabled = true;
    this.classList.add('ms-upsell--busy');

    try {
      const added = await fetch(`${this.#root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(button.dataset.variantId), quantity: 1 }] }),
      });
      if (!added.ok) throw new Error('add failed');

      const cart = await (await fetch(`${this.#root}cart.js`)).json();

      // Horizon's cart-items-component re-renders the whole section when a cart:update
      // arrives without pre-rendered section HTML — which keeps the progress bar, this
      // block and the stock chips in sync without any work here.
      const update = new Event('cart:update', { bubbles: true });
      update.detail = {
        resource: cart,
        sourceId: 'cart-upsell',
        data: { source: 'cart-upsell', itemCount: cart.item_count },
      };
      document.dispatchEvent(update);
    } catch {
      button.disabled = false;
      this.classList.remove('ms-upsell--busy');
    }
  };
}

/* -------------------------------------------------------------------------- */

function countSameLine(candidates) {
  return candidates.reduce((count, candidate) => count + (candidate.sameLine ? 1 : 0), 0);
}

/**
 * One seed per product line, not per cart line: two Sébium products in the cart should not
 * take two of the six tiles. The most recently added product of a line is kept, because it
 * is the one the shopper was just looking at.
 */
function byLine(seeds) {
  const keep = new Map();
  seeds.forEach((seed) => {
    const { one } = lineTokens(seed.title, seed.vendor);
    keep.set(one || seed.id, seed);
  });
  return [...keep.values()];
}

function interleave(lists, target) {
  const picks = [];
  const used = new Set();

  for (let round = 0; round < 4 && picks.length < target; round += 1) {
    let progressed = false;
    for (const list of lists) {
      if (picks.length >= target) break;
      const next = list.find((candidate) => !used.has(candidate.product.id));
      if (!next) continue;
      used.add(next.product.id);
      picks.push(next);
      progressed = true;
    }
    if (!progressed) break;
  }

  return picks;
}

function rank(products, seed, tokens, inCart) {
  const seen = new Set();
  const out = [];

  products.forEach((product, order) => {
    if (!product || seen.has(product.id)) return;
    if (product.id === seed.id || inCart.has(product.id) || !product.available) return;
    if (product.variantCount !== null && product.variantCount !== 1) return;
    seen.add(product.id);

    const own = lineTokens(product.title, product.vendor);
    let score = 0;
    if (tokens.two && own.two === tokens.two) score = 4;
    else if (tokens.one && own.one === tokens.one) score = 3;
    else if (norm(product.vendor) === norm(seed.vendor)) score = 1;

    // The same product one size up is a weaker suggestion than a different product from
    // the same line, but still better than a stranger from the same brand.
    if (score >= 3 && sameProductOtherSize(product.title, seed.title)) score = 2;

    out.push({ product, seed, score, order, sameLine: score >= 3, level: score === 4 ? 'sub' : 'line' });
  });

  out.sort((a, b) => b.score - a.score || a.order - b.order);
  return out;
}

function norm(value) {
  return (value || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
}

function cleanWord(word) {
  return word.replace(/^[^\p{L}\p{N}]+|[^\p{L}\p{N}+]+$/gu, '');
}

function withoutVendor(title, vendor) {
  const clean = (title || '').trim();
  const brand = (vendor || '').trim();
  return brand && norm(clean).startsWith(`${norm(brand)} `) ? clean.slice(brand.length + 1) : clean;
}

/** "BIODERMA Photoderm XDefense …" → { one: "photoderm", two: "photoderm xdefense" } */
function lineTokens(title, vendor) {
  const words = withoutVendor(title, vendor).split(/\s+/).map(cleanWord).filter((word) => word.length >= 2);
  if (words.length === 0) return { one: '', two: '' };
  return {
    one: norm(words[0]),
    two: words.length > 1 ? norm(`${words[0]} ${words[1]}`) : '',
  };
}

/**
 * The label on the tile — the line the suggestion actually came from, not the seed's full
 * name: an Intensive Hyaluronic+ tile beside an Intensive NAD+ cart line says "Intensive",
 * because that is the line the two share. "sub" is for a match on both words.
 */
function lineLabel(title, vendor, level) {
  const words = withoutVendor(title, vendor).split(/\s+/).map(cleanWord).filter((word) => word.length >= 2);
  if (words.length === 0) return '';
  const sub = level === 'sub' && /[+\d]/.test(words[1] || '');
  return sub ? `${words[0]} ${words[1]}` : words[0];
}

function sameProductOtherSize(a, b) {
  const strip = (value) => norm(value).replace(/\d+([.,]\d+)?\s?(ml|l|g|gab|kg)\b/g, '').replace(/\s+/g, ' ').trim();
  return strip(a) === strip(b);
}

/**
 * Money arrives in two shapes: recommendations give cents (1194), /search/suggest.json gives
 * a decimal string ("12.60"). Search also carries no `compare_at_price` at all — only the
 * compare_at_price_min/max pair, as strings.
 */
function toCents(value) {
  if (value === null || value === undefined || value === '') return 0;
  const cents = typeof value === 'string' ? Math.round(Number.parseFloat(value) * 100) : Number(value);
  return Number.isFinite(cents) ? cents : 0;
}

function normalize(raw) {
  const variants = raw.variants || [];
  const price = toCents(raw.price);
  const compareAt = toCents(raw.compare_at_price ?? raw.compare_at_price_min ?? variants[0]?.compare_at_price);
  let image = raw.featured_image || raw.image || '';
  if (image && typeof image === 'object') image = image.url || image.src || '';
  if (typeof image === 'string' && image.startsWith('//')) image = `https:${image}`;

  return {
    id: String(raw.id),
    title: raw.title || '',
    vendor: raw.vendor || '',
    handle: raw.handle || '',
    url: raw.url || (raw.handle ? `/products/${raw.handle}` : ''),
    price,
    // Only a compare-at that is actually higher counts as a sale: Shopify leaves the field
    // sitting at the price itself, or at 0, on products that are not discounted.
    compareAt: compareAt > price ? compareAt : 0,
    available: raw.available !== false,
    image: typeof image === 'string' ? image : '',
    variantId: variants.length === 1 ? String(variants[0].id) : null,
    variantCount: variants.length > 0 ? variants.length : null,
  };
}

async function getJson(url) {
  try {
    const response = await fetch(url);
    if (!response.ok) return null;
    return await response.json();
  } catch {
    return null;
  }
}

function tile(pick) {
  const product = pick.product;
  const image = product.image
    ? `${product.image}${product.image.includes('?') ? '&' : '?'}width=200`
    : '';
  const label = pick.sameLine ? lineLabel(pick.seed.title, pick.seed.vendor, pick.level) : '';

  return `
    <div class="ms-upsell__tile">
      ${label ? `<span class="ms-upsell__tag">${escapeHtml(label)}</span>` : ''}
      <a class="ms-upsell__link" href="${escapeHtml(product.url)}" tabindex="-1" aria-hidden="true">
        ${image ? `<img class="ms-upsell__image" src="${escapeHtml(image)}" alt="" loading="lazy" width="200" height="200">` : ''}
      </a>
      <p class="ms-upsell__name">${escapeHtml(withoutVendor(product.title, product.vendor))}</p>
      <div class="ms-upsell__foot">
        <span class="ms-upsell__prices">
          <span class="ms-upsell__price">${escapeHtml(money(product.price))}</span>
          ${product.compareAt
            ? `<s class="ms-upsell__was"><span class="visually-hidden">Parastā cena </span>${escapeHtml(money(product.compareAt))}</s>`
            : ''}
        </span>
        <button
          type="button"
          class="ms-upsell__add"
          data-variant-id="${escapeHtml(String(product.variantId))}"
          aria-label="Pievienot grozam: ${escapeHtml(product.title)}"
        >+</button>
      </div>
    </div>
  `;
}

function money(cents) {
  return `€${(cents / 100).toFixed(2).replace('.', ',')}`;
}

function escapeHtml(value) {
  return String(value).replace(/[&<>"']/g, (char) => {
    return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[char];
  });
}

if (!customElements.get('cart-upsell')) {
  customElements.define('cart-upsell', CartUpsell);
}
